import React from 'react';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns'; // needed for the TimeScale

import {
  Chart as ChartJS,
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
} from 'chart.js';

import { drawLinePlugin } from '@UI-Elements/ChartPlugins/DrawLinePlugin';
import { handleMouseDown, handleMouseMove, handleMouseUp } from '@UI-Elements/ChartPlugins/ChartUtils';
import { calcRouteColor } from './MaterialDetailUtils';

ChartJS.register(
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin,
);

const getOptions = (onDataClick: (dataIndex: number) => void): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: false,
    title: { display: false },
    tooltip: { mode: 'index', intersect: false },
    zoom: {
      zoom: {
        wheel: {
          enabled: true,
        },
        pinch: {
          enabled: true,
        },
        mode: 'xy',
      },
      pan: {
        enabled: true,
        mode: 'xy',
      },
    },
  },
  onHover: (event: any) => {
    if (event.type === 'click') {
      const activeElements = event.chart.tooltip?.getActiveElements();
      if (activeElements?.length) {
        const {
          index: dataIndex,
          element: { x },
        } = activeElements[0];
        const topY = event.chart.scales.y.top;
        const bottomY = event.chart.scales.y.bottom;
        const clickedData = { dataIndex, topY, bottomY, x };
        event.chart.clicked = clickedData;
        onDataClick(dataIndex);
      }
    }
  },
  scales: {
    x: {
      type: 'time' as const,
      time: {
        unit: 'month' as const,
        displayFormats: {
          month: 'MM/yy',
        },
      },
      title: {
        display: false,
        text: 'Month',
      },
      ticks: { display: true, autoSkip: true },
      grid: { display: false },
    },
    y: {
      beginAtZero: false,
      offset: true,
      title: { display: true, text: 'Price per ton (USD)' },
    },
  },
  elements: { line: { tension: 0.6 }, point: { radius: 0 } },
});

const routeBounds: Record<string, { high: string; low: string }> = {
  route1: { high: 'r1High', low: 'r1Low' },
  route2: { high: 'r2High', low: 'r2Low' },
  route3: { high: 'r3High', low: 'r3Low' },
};
const getHigh = (routeName: string): string => routeBounds[routeName]?.high || 'r1High';
const getLow = (routeName: string): string => routeBounds[routeName]?.low || 'r1Low';

const getData = (materialRoutes: any[], route: string, routeColor: string): ChartData<'line'> => ({
  labels: materialRoutes.map((r) => r.date), //priceData.map((d) => dateString(d.date)),
  datasets: [
    {
      label: 'Price per ton',
      data: materialRoutes.map((d) => ({ x: new Date(d.date).getTime(), y: d.price })),
      borderColor: '#5467b2', //'rgb(53, 162, 235)',
      backgroundColor: 'rgba(53, 162, 235, 0.5)',
      fill: false,
      tension: 0.4,
      borderWidth: 2,
    },
    {
      label: 'Route price',
      data: materialRoutes.map((d) => ({ x: new Date(d.date).getTime(), y: d[route] })),
      borderColor: routeColor || 'rgba(103, 153, 224,1)',
      backgroundColor: 'rgba(53, 162, 235, 0)',
      fill: false,
      tension: 0.4,
      borderWidth: 1,
    },
    {
      label: 'High risk',
      data: materialRoutes.map((d) => ({ x: new Date(d.date).getTime(), y: d.High })),
      borderColor: 'rgba(255, 99, 132, 0)',
      backgroundColor: 'rgba(255, 99, 132, 0.2)',
      fill: '+1',
      borderWidth: 1,
      tension: 0.4,
    },
    {
      label: 'Hedged high risk',
      data: materialRoutes.map((d) => ({ x: new Date(d.date).getTime(), y: d[getHigh(route)] })),
      borderColor: 'rgba(255, 99, 132, 0)',
      backgroundColor: 'rgba(255, 99, 132, 0.4)',
      fill: '+1',
      borderWidth: 1,
      tension: 0.4,
    },

    {
      label: 'Hedged low risk',
      data: materialRoutes.map((d) => ({ x: new Date(d.date).getTime(), y: d[getLow(route)] })),
      borderColor: 'rgba(255, 99, 132, 0)',
      backgroundColor: 'rgba(255, 99, 132, 0.4)',
      fill: '-1',
      borderWidth: 1,
      tension: 0.4,
    },
    {
      label: 'Low risk',
      data: materialRoutes.map((d) => ({ x: new Date(d.date).getTime(), y: d.Low })),
      borderColor: 'rgba(255, 99, 132, 0)',
      backgroundColor: 'rgba(255, 99, 132, 0.2)',
      fill: '-1',
      tension: 0.4,
    },
  ],
});

interface Props {
  prices: any[];
  showRoute: string;
  onDataClick: (dataIndex: number) => void;
}
export const HistoryChart: React.FC<Props> = ({ prices, showRoute, onDataClick }) => {
  React.useEffect(() => {
    //@ts-expect-error-type-error
    const chart = chartRef.current as ChartJS<'line'>;
    if (chart?.canvas) {
      chart.canvas.addEventListener('mousedown', handleMouseDown(chart));
      chart.canvas.addEventListener('mousemove', handleMouseMove(chart));
      chart.canvas.addEventListener('mouseup', handleMouseUp(chart));
    }

    return () => {
      if (chart?.canvas) {
        chart.canvas.removeEventListener('mousemove', handleMouseMove(chart));
        chart.canvas.removeEventListener('mousedown', handleMouseDown(chart));
        chart.canvas.removeEventListener('mouseup', handleMouseUp(chart));
      }
    };
  }, []);

  const chartRef = React.useRef();

  return (
    <div className="flex flex-col flex-1 h-full  ">
      <div className="flex flex-row justify-between text-sm">
        <p className="ps-[4rem]">History</p>
        <p>Future</p>
      </div>
      <div className="h-full ">
        <Line
          options={getOptions(onDataClick)}
          data={getData(prices, showRoute, calcRouteColor(showRoute))}
          plugins={[drawLinePlugin]}
          ref={chartRef}
        />
      </div>
    </div>
  );
};
